/**
 * SESSIONE DELLE PAGINE EMBED — il gettone in ingresso, e le regole del vivere in un iframe.
 *
 * Queste pagine vivono dentro l'iframe di un sistema esterno (ATOM). Tre cose cambiano
 * rispetto al resto del gestionale:
 *
 * 1. SI ARRIVA CON UN GETTONE, non con una sessione: `?t=<gettone>` emesso dal backend
 *    su richiesta firmata del sistema esterno. Lo si scambia subito con /auth/handoff/exchange
 *    (stesso scambio del passaggio v1→v2) e lo si toglie dall'indirizzo.
 *
 * 2. LO STORAGE PUO' NON ESSERCI. In un iframe di terza parte Safari puo' negare
 *    localStorage/sessionStorage con un SecurityError. Tutte le letture/scritture passano
 *    da qui, dentro try/catch: senza storage la pagina lo dice invece di morire bianca.
 *
 * 2-bis. ┌─ 🔴 IL GETTONE EMBED NON TOCCA MAI localStorage ────────────────────────────┐
 *        │                                                                              │
 *        │ localStorage e' UNO PER DOMINIO, condiviso da tutte le schede aperte su      │
 *        │ CLTV. Scriverci il gettone embed — che vale SOLO per queste due pagine —     │
 *        │ significa sostituire la sessione di lavoro dell'utente ovunque: da quel      │
 *        │ momento ogni altra pagina del gestionale risponde NO_PERMISSION, e l'utente  │
 *        │ non capisce perche'. Dentro l'iframe non succede (il browser separa lo       │
 *        │ storage per sito genitore), ma basta il pulsante "apri in una scheda nuova"  │
 *        │ per farlo succedere davvero.                                                  │
 *        │                                                                              │
 *        │ Quindi: il gettone sta in sessionStorage, che e' PER SCHEDA. Le API del      │
 *        │ gestionale pero' leggono tutte `localStorage.getItem('hash')` — 32 file, non │
 *        │ si toccano. Si intercetta `window.fetch` e si sostituisce l'intestazione     │
 *        │ x-access-token in uscita: le API restano come sono, e la sessione dell'utente│
 *        │ resta la sua.                                                                 │
 *        └──────────────────────────────────────────────────────────────────────────────┘
 *
 * 3. ALLA SCADENZA NON SI VA AL LOGIN. Il redirect al login del gestionale, dentro un
 *    iframe, e' una schermata assurda. Si avvisa il genitore con postMessage
 *    ({type: "atom-embed-refresh"}) e quello ricarica l'iframe con un gettone fresco.
 *    L'utente non vede niente.
 */

/* ── Storage difensivo ──────────────────────────────────────────────────────── */

/* Di proposito NON ci sono scorciatoie per localStorage: da qui dentro non si scrive.
   Vedi la nota 2-bis in cima. */

export function safeGetSession(chiave) {
    try {
        return sessionStorage.getItem(chiave);
    } catch (e) {
        return null;
    }
}

export function safeSetSession(chiave, valore) {
    try {
        sessionStorage.setItem(chiave, valore);
        return true;
    } catch (e) {
        return false;
    }
}

/* ── L'intestazione in uscita ───────────────────────────────────────────────── */

/** Dove sta il gettone embed: sessionStorage, quindi solo in questa scheda. */
const CHIAVE_GETTONE = "embedHash";

/** Un intercettore solo, anche se la pagina si ricarica o qualcuno richiama la funzione. */
let intercettoreInstallato = false;

/**
 * Fa in modo che ogni chiamata delle API del gestionale parta con il gettone embed
 * invece di quello in localStorage.
 *
 * Sostituisce l'intestazione SOLO se la richiesta ne porta gia' una: cosi' lo scambio
 * del gettone (/auth/handoff/exchange, che va chiamato senza token) resta intatto, e
 * nessuna chiamata anonima si ritrova autenticata per sbaglio.
 *
 * ⚠️ Vale per `fetch`. Le due pagine embed usano solo quello; se un domani ci finisse
 * dentro una tabella che chiama con axios/XMLHttpRequest, quella chiamata partirebbe
 * con il token di localStorage e verrebbe rifiutata dal confinamento lato server.
 */
function installaIntercettoreFetch() {
    if (intercettoreInstallato) return;
    if (typeof window === "undefined" || typeof window.fetch !== "function") return;

    const originale = window.fetch.bind(window);

    window.fetch = function (risorsa, opzioni) {
        const o = opzioni || {};
        let intestazioni;
        try {
            intestazioni = new Headers(o.headers || {});
        } catch (e) {
            // Intestazioni in una forma che Headers non digerisce: si lascia passare.
            return originale(risorsa, opzioni);
        }

        if (!intestazioni.has("x-access-token"))
            return originale(risorsa, opzioni);

        const gettone = safeGetSession(CHIAVE_GETTONE);
        if (!gettone)
            return originale(risorsa, opzioni);

        intestazioni.set("x-access-token", gettone);
        return originale(risorsa, Object.assign({}, o, { headers: intestazioni }));
    };

    intercettoreInstallato = true;
}

/* ── Il canale verso il genitore ────────────────────────────────────────────── */

/**
 * Chiede al sistema che ci incorpora un ingresso fresco. Non contiene niente di
 * segreto, quindi il destinatario e' "*": non sappiamo su che dominio giri ATOM,
 * e un messaggio che dice solo "ricaricami" puo' leggerlo chiunque senza danno.
 */
export function chiediIngressoFresco() {
    try {
        if (window.parent && window.parent !== window) {
            window.parent.postMessage({ type: "atom-embed-refresh" }, "*");
        }
    } catch (e) { /* fuori da un iframe non c'e' nessuno da avvisare */ }
}

/** Siamo davvero dentro un iframe? */
export function dentroIframe() {
    try {
        return window.self !== window.top;
    } catch (e) {
        // L'accesso a window.top e' negato = siamo in un iframe cross-origin.
        return true;
    }
}

/* ── Scambio del gettone ────────────────────────────────────────────────────── */

/**
 * Legge `?t=` dall'indirizzo e lo TOGLIE subito (replaceState): il gettone non deve
 * restare nella barra, nella cronologia o in uno screenshot.
 * @returns {string|null}
 */
export function estraiGettone() {
    try {
        const params = new URLSearchParams(window.location.search);
        const t = params.get("t");
        if (t) {
            params.delete("t");
            const pulito = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
            window.history.replaceState({}, document.title, pulito);
        }
        return t || null;
    } catch (e) {
        return null;
    }
}

/**
 * Scambia il gettone con una sessione e riempie hash + userData.
 *
 * Lo scambio torna solo i dati base dell'utente: per far funzionare i componenti del
 * gestionale (che leggono ruolo e permessi da userData) si richiama subito
 * /auth/permission con il token appena ottenuto, e si salva la risposta INTERA —
 * la stessa forma che scriverebbe il bootstrap normale.
 *
 * @returns {Promise<object>} lo userData completo
 */
export function scambiaGettone(gettone) {
    return fetch(global.route.authHandoffExchange, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: gettone })
    })
        .then((r) => r.json())
        .then((j) => {
            if (j.status !== "HANDOFF_OK" || !j.accessToken)
                return Promise.reject(j);

            // ⚠️ sessionStorage, NON localStorage: vedi la nota 2-bis in cima.
            if (!safeSetSession(CHIAVE_GETTONE, j.accessToken)) {
                // Storage negato: senza un posto dove tenere il gettone, le chiamate delle
                // tabelle partirebbero con quello sbagliato (o senza).
                return Promise.reject({ status: "EMBED_NO_STORAGE" });
            }
            installaIntercettoreFetch();

            // Il contesto del record (es. il lead ATOM per "assegna-classe"), se il gettone
            // lo portava. Per scheda, come il gettone: sparisce quando si chiude la sessione
            // embed, non deve sopravvivere a un ingresso diverso nella stessa scheda.
            safeSetSession("embedContext", JSON.stringify(j.context || null));

            return fetch(global.route.permission, {
                headers: { "x-access-token": j.accessToken },
                method: "GET"
            })
                .then((r) => (r.status !== 500) ? r.json() : Promise.reject(j))
                .then((userData) => {
                    if (typeof userData.status !== "undefined")
                        return Promise.reject(userData);
                    safeSetSession("userData", JSON.stringify(userData));
                    return userData;
                });
        });
}

/**
 * C'e' gia' una sessione utilizzabile? (Il caso: ricarica della pagina dopo che il
 * gettone e' gia' stato speso e tolto dall'indirizzo.)
 * @returns {object|null} lo userData, se c'e'
 */
export function sessioneEsistente() {
    const gettone = safeGetSession(CHIAVE_GETTONE);
    const grezzo = safeGetSession("userData");
    if (!gettone || !grezzo) return null;
    try {
        const userData = JSON.parse(grezzo);
        // La pagina si e' ricaricata: il fetch e' quello nuovo del browser, va riarmato.
        installaIntercettoreFetch();
        return userData;
    } catch (e) {
        return null;
    }
}

/**
 * Butta via la sessione embed di QUESTA scheda. Non tocca `hash`: quello e' la sessione
 * di lavoro dell'utente, e non e' mai stata nostra.
 */
export function chiudiSessioneEmbed() {
    try {
        sessionStorage.removeItem(CHIAVE_GETTONE);
        sessionStorage.removeItem("userData");
        sessionStorage.removeItem("embedContext");
    } catch (e) { /* storage negato: non c'era niente da togliere */ }
}

/**
 * Il contesto del record con cui si e' entrati (es. {type:"lead", email, phone, name}),
 * o null se il gettone non lo portava (le pagine di sola consultazione non lo usano mai).
 */
export function contestoEmbed() {
    const grezzo = safeGetSession("embedContext");
    if (!grezzo) return null;
    try {
        return JSON.parse(grezzo);
    } catch (e) {
        return null;
    }
}

/**
 * Sostituisce il gestore globale degli esiti API per la durata della pagina embed:
 * UNAUTHORIZED non manda al login (vedi la nota 3 in cima), avvisa il genitore.
 *
 * ⚠️ Da chiamare nel mount della pagina embed. Non serve ripristinare: le pagine
 * embed vivono da sole in una scheda/iframe dedicato, non si naviga altrove.
 */
export function installaCheckAuthEmbed(onScaduta) {
    global.checkAuth = function (status) {
        if (status === "UNAUTHORIZED") {
            chiediIngressoFresco();
            if (typeof onScaduta === "function") onScaduta();
        }
    };
}
